import {
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X } from "../../pages/overview/icons";

/* Gateway-styled modal (the shared DS Dialog is themed for the rest of the
 * app). Rendered in a portal wrapped in `.tw-app` so
 * the page's --text/--surface/--border tokens resolve; Escape and scrim-click
 * close (unless busy), and focus moves in on open. Used by the redesigned
 * settings surfaces (API keys, Members). */
export function TokenwiseModal({
  open,
  title,
  description,
  busy = false,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  busy?: boolean;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (!open) return;

    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => prev?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  return createPortal(
    // .tw-app provides the page's design tokens to the portal, but that class
    // also paints an opaque --page background; force it transparent here so the
    // scrim below is the only tint and the app shows through.
    <div
      className="tw-app fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: "transparent" }}
    >
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden
        onClick={busy ? undefined : onClose}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className="relative w-full max-w-md rounded-[5px] p-5 outline-none"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          boxShadow: "0 18px 48px rgba(0,0,0,0.22)",
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            id={titleId}
            className="text-[15px] font-medium text-[var(--text)]"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="flex-shrink-0 text-[var(--text-muted)] transition-colors hover:text-[var(--text)]"
          >
            <X size={16} />
          </button>
        </div>
        {description && (
          <div
            id={descId}
            className="mt-2 text-[13px] leading-relaxed text-[var(--text-muted)]"
          >
            {description}
          </div>
        )}
        {children && <div className="mt-4">{children}</div>}
        {footer && (
          <div className="mt-5 flex justify-end gap-2.5">{footer}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/* Modal footer buttons in the design's CTA language: the primary is the dark
 * rounded chip (page-inverted), the danger keeps the crimson. */
export function ModalButton({
  variant = "secondary",
  disabled,
  onClick,
  children,
}: {
  variant?: "secondary" | "brand" | "danger";
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const styles: Record<string, CSSProperties> = {
    secondary: { border: "1px solid var(--border-2)", color: "var(--text)" },
    brand: { background: "var(--text)", color: "var(--page)" },
    danger: { background: "var(--val-crimson)", color: "#fff" },
  };
  const isSolid = variant !== "secondary";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-[2px] px-4 py-2 text-[13px] transition-opacity disabled:opacity-50 ${
        isSolid ? "hover:opacity-90" : "hover:bg-[var(--hover)]"
      }`}
      style={styles[variant]}
    >
      {children}
    </button>
  );
}
